import { Box, Flex, Text, VStack, Button, useBreakpointValue } from '@skin-seoul/design-system';
import { useState } from 'react';
import type { SelectOption } from './common/Select';
import Select from './common/Select';
import { MultipleSelect } from './common/MultipleSelect';
import { CloseIcon } from '../assets/icons/CloseIcon';
import { useProductService } from '@skin-seoul/service';

interface TagProps {
  label: string;
  value: string;
  onRemove: (value: string) => void;
}

const Tag = ({ label, value, onRemove }: TagProps) => (
  <Flex
    align="center"
    justify="center"
    bg="#1c1c1c"
    borderRadius="12px"
    border="1px solid #e2e2e2"
    px="12px"
    py="10px"
    gap="10px"
    color="#dcdcdc"
    fontFamily="Plus Jakarta Sans"
    style={{ fontSize: '12px' }}
    fontWeight="400"
    transition="background 0.15s"
    _hover={{ bg: '#313131' }}
  >
    <Text>{label}</Text>
    <Button
      aria-label="deselect"
      bg="none"
      border="none"
      color="#b0b0b8"
      ml="4px"
      p="0"
      w="18px"
      h="18px"
      borderRadius="50%"
      transition="color 0.15s"
      _hover={{ color: '#3d1690' }}
      onClick={() => onRemove(value)}
    >
      <CloseIcon />
    </Button>
  </Flex>
);

export const sortOptions: SelectOption[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'highest', label: 'Highest Rating' },
  { value: 'lowest', label: 'Lowest Rating' },
];

interface ReviewFilterBarProps {
  onSortChange: (value: string) => void;
  onSkinTypeChange: (value: string[]) => void;
  onSkinConcernChange: (value: string[]) => void;
}

export const ReviewFilterBar = ({ onSortChange, onSkinTypeChange, onSkinConcernChange }: ReviewFilterBarProps) => {
  const [sort, setSort] = useState<string>('');
  const [skinTypes, setSkinTypes] = useState<string[]>([]);
  const [skinConcerns, setSkinConcerns] = useState<string[]>([]);
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const { useGetProductTaxonomy } = useProductService();

  const { data: taxonomy } = useGetProductTaxonomy();

  const skinTypeOptions: SelectOption[] =
    taxonomy?.skinType?.map((item) => ({
      label: item.name.split(' ')?.at(0) || '',
      value: item.name.split(' ')?.at(0) || '',
    })) || [];
  const skinConcernOptions: SelectOption[] =
    taxonomy?.skinConcern?.map((item) => ({
      label: item.name,
      value: item.name,
    })) || [];

  const handleSortChange = (value: string) => {
    setSort(value);
    onSortChange(value);
  };

  const handleSkinTypeChange = (values: string[]) => {
    setSkinTypes(values);
    onSkinTypeChange(values);
  };

  const handleSkinConcernChange = (values: string[]) => {
    setSkinConcerns(values);
    onSkinConcernChange(values);
  };

  const renderTags = () => (
    <>
      {sort && (
        <Tag
          key={sort}
          label={sortOptions.find((opt) => opt.value === sort)?.label || ''}
          value={sort}
          onRemove={() => {
            setSort('');
            onSortChange('');
          }}
        />
      )}
      {skinTypeOptions
        .filter((opt) => skinTypes.includes(opt.value))
        .map((opt) => (
          <Tag
            key={opt.value}
            label={opt.label}
            value={opt.value}
            onRemove={(value) => {
              const newValues = skinTypes.filter((v) => v !== value);
              setSkinTypes(newValues);
              onSkinTypeChange(newValues);
            }}
          />
        ))}
      {skinConcernOptions
        .filter((opt) => skinConcerns.includes(opt.value))
        .map((opt) => (
          <Tag
            key={opt.value}
            label={opt.label}
            value={opt.value}
            onRemove={(value) => {
              const newValues = skinConcerns.filter((v) => v !== value);
              setSkinConcerns(newValues);
              onSkinConcernChange(newValues);
            }}
          />
        ))}
    </>
  );

  const renderDropdowns = () => (
    <>
      <Select options={sortOptions} value={sort} onChange={handleSortChange} title="SortBy" />
      <MultipleSelect options={skinTypeOptions} values={skinTypes} onChange={handleSkinTypeChange} title="Skin type" />
      <MultipleSelect
        options={skinConcernOptions}
        values={skinConcerns}
        onChange={handleSkinConcernChange}
        title="Skin concern"
      />
    </>
  );

  if (isMobile) {
    return (
      <VStack align="stretch" gap="0">
        <Box display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap="4px" w="full" overflowX="auto">
          {renderDropdowns()}
        </Box>
        {(sort || skinTypes.length > 0 || skinConcerns.length > 0) && (
          <Flex flexWrap="wrap" gap="4px" mt="14px">
            {renderTags()}
          </Flex>
        )}
      </VStack>
    );
  }

  return (
    <Flex align="center" gap="8px" flexWrap="wrap" w="full">
      <Box display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap="4px" w="full">
        {renderDropdowns()}
      </Box>
      {(sort || skinTypes.length > 0 || skinConcerns.length > 0) && (
        <Flex flexWrap="wrap" gap={1}>
          {renderTags()}
        </Flex>
      )}
    </Flex>
  );
};

export default ReviewFilterBar;
